import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Stars } from "../components/Stars";
import { Save, CalendarClock, Video, CalendarX, X, MessageSquareQuote, Eye, EyeOff, Trash2 } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = Array.from({ length: 49 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const label12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  if (h === 24) return "Midnight";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

const TimeSelect = ({ value, onChange, testid, options }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="wwr-input rounded-xl px-3 py-2.5 text-sm">
    {options.map((t) => <option key={t} value={t}>{label12(t)}</option>)}
  </select>
);

const Upcoming = ({ list }) => (
  <div className="card-surface p-6 md:p-8" data-testid="upcoming-sessions">
    <h2 className="font-display text-2xl mb-4 inline-flex items-center gap-2"><CalendarClock size={20} className="text-amber-300" /> Upcoming sessions</h2>
    {list.length === 0 ? <p className="text-sm text-neutral-500">No booked sessions yet.</p> : (
      <ul className="divide-y divide-white/5">
        {list.map((u) => (
          <li key={u.slot_start} className="py-3 text-sm" data-testid={`upcoming-${u.slot_start}`}>
            <div className="text-neutral-100">{u.slot_label}{u.gift_from && <span className="ml-2 rounded-full bg-amber-400/15 text-amber-200 text-[10px] px-2 py-0.5" data-testid={`gift-badge-${u.slot_start}`}>Gift from {u.gift_from}</span>}</div>
            <div className="text-neutral-400">{u.name} · {u.email}</div>
            {u.topics && <div className="text-neutral-500 text-xs mt-1">{u.topics}</div>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const BlockedDates = ({ dates, onChange }) => {
  const [d, setD] = useState("");
  const add = () => { if (d && !dates.includes(d)) onChange([...dates, d].sort()); setD(""); };
  const pretty = (x) => new Date(`${x}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  return (
    <div data-testid="blocked-dates">
      <div className="text-sm text-neutral-300 mb-3 inline-flex items-center gap-2"><CalendarX size={14} /> Blocked dates (vacation or busy days)</div>
      <div className="flex gap-2 mb-3">
        <input type="date" value={d} min={new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" })} aria-label="Date to block" onChange={(e) => setD(e.target.value)}
          data-testid="blocked-date-input" className="wwr-input rounded-xl px-3 py-2.5 text-sm" />
        <button type="button" onClick={add} disabled={!d} data-testid="blocked-date-add"
          className="rounded-xl border border-white/15 px-4 text-sm text-neutral-200 hover:border-amber-400/60 disabled:opacity-40">Block</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {dates.length === 0 && <span className="text-xs text-neutral-500">No blocked dates.</span>}
        {dates.map((x) => (
          <span key={x} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 pl-3 pr-1.5 py-1 text-xs text-neutral-200" data-testid={`blocked-${x}`}>
            {pretty(x)}
            <button type="button" onClick={() => onChange(dates.filter((y) => y !== x))} aria-label={`Unblock ${x}`} data-testid={`unblock-${x}`}
              className="rounded-full p-0.5 hover:bg-white/10"><X size={12} /></button>
          </span>
        ))}
      </div>
      <p className="text-xs text-neutral-500 mt-2">Remember to hit Save after changes.</p>
    </div>
  );
};

const TestimonialsAdmin = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/testimonials").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const toggle = async (t) => { await api.patch(`/admin/testimonials/${t.id}`, { approved: !t.approved }); load(); };
  const remove = async (t) => { if (window.confirm("Delete this testimonial?")) { await api.delete(`/admin/testimonials/${t.id}`); load(); } };
  return (
    <div className="card-surface p-6 md:p-8" data-testid="testimonials-admin">
      <h2 className="font-display text-2xl mb-1 inline-flex items-center gap-2"><MessageSquareQuote size={20} className="text-amber-300" /> Testimonials</h2>
      <p className="text-xs text-neutral-500 mb-4">People are asked for one about 45 minutes after their call. Only ones you show appear on the homepage.</p>
      {list.length === 0 ? <p className="text-sm text-neutral-500">None yet.</p> : (
        <ul className="divide-y divide-white/5">
          {list.map((t) => (
            <li key={t.id} className="py-4 flex gap-4" data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex-1 min-w-0">
                {t.rating && <Stars value={t.rating} size={13} testid={`admin-stars-${t.id}`} />}
                <p className="text-sm text-neutral-200">"{t.text}"</p>
                <p className="text-xs text-neutral-500 mt-1">— {t.display_name} · {t.email} · {t.approved ? <span className="text-teal-300">Showing</span> : "Hidden"}</p>
              </div>
              <button onClick={() => toggle(t)} data-testid={`testimonial-toggle-${t.id}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs inline-flex items-center gap-1.5 h-fit hover:border-teal-400/60">
                {t.approved ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
              </button>
              <button onClick={() => remove(t)} data-testid={`testimonial-delete-${t.id}`} aria-label="Delete" className="rounded-full border border-white/15 p-2 h-fit hover:border-red-400/60"><Trash2 size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const StoriesAdmin = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/stories").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const run = (p) => p.then(load).catch(() => toast.error("Could not update. Please try again."));
  const setStatus = (s, status) => run(api.patch(`/admin/stories/${s.id}`, { status }));
  const remove = (s) => window.confirm("Delete this moment?") && run(api.delete(`/admin/stories/${s.id}`));
  const tone = { featured: "text-teal-300", hidden: "text-neutral-500", pending: "text-amber-300" };
  return (
    <div className="card-surface p-6 md:p-8" data-testid="stories-admin">
      <h2 className="font-display text-2xl mb-1">Recovery moments</h2>
      <p className="text-xs text-neutral-500 mb-4">Shared by members. Featured ones appear next to Artemis on the homepage.</p>
      {list.length === 0 ? <p className="text-sm text-neutral-500">None yet.</p> : (
        <ul className="divide-y divide-white/5">
          {list.map((s) => (
            <li key={s.id} className="py-4 flex gap-3 flex-wrap sm:flex-nowrap" data-testid={`admin-story-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-200">"{s.text}"</p>
                <p className="text-xs text-neutral-500 mt-1">— {s.display_name} · {s.email} · <span className={tone[s.status]}>{s.status}</span></p>
              </div>
              {s.status !== "featured" ? (
                <button onClick={() => setStatus(s, "featured")} data-testid={`story-feature-${s.id}`} className="rounded-full border border-teal-400/40 px-3 py-1.5 text-xs h-fit hover:bg-teal-500/10">Feature</button>
              ) : (
                <button onClick={() => setStatus(s, "hidden")} data-testid={`story-hide-${s.id}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs h-fit hover:border-white/40">Hide</button>
              )}
              <button onClick={() => remove(s)} data-testid={`story-delete-${s.id}`} aria-label="Delete" className="rounded-full border border-white/15 p-2 h-fit hover:border-red-400/60"><Trash2 size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ToolkitRatingsAdmin = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/toolkit-ratings").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const toggle = (r) => api.patch(`/admin/toolkit-ratings/${r.id}`, { approved: !r.approved }).then(load).catch(() => toast.error("Could not update."));
  return (
    <div className="card-surface p-6 md:p-8" data-testid="toolkit-ratings-admin">
      <h2 className="font-display text-2xl mb-1">Toolkit ratings</h2>
      <p className="text-xs text-neutral-500 mb-4">Star ratings count right away. Written notes only show on toolkit cards when you tap Show.</p>
      {list.length === 0 ? <p className="text-sm text-neutral-500">No ratings yet.</p> : (
        <ul className="divide-y divide-white/5">
          {list.map((r) => (
            <li key={r.id} className="py-3 flex gap-3" data-testid={`admin-rating-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><Stars value={r.rating} size={12} testid={`admin-rating-stars-${r.id}`} /><span className="text-xs text-neutral-400">{r.title}</span></div>
                {r.text && <p className="text-sm text-neutral-200 mt-1">"{r.text}"</p>}
                <p className="text-xs text-neutral-500 mt-1">— {r.display_name} · {r.email}</p>
              </div>
              {r.text && (
                <button onClick={() => toggle(r)} data-testid={`rating-toggle-${r.id}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs h-fit hover:border-teal-400/60">
                  {r.approved ? "Hide" : "Show"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function AdminSessions() {
  const [cfg, setCfg] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/session-settings")
      .then(({ data }) => { setCfg(data.settings); setUpcoming(data.upcoming); })
      .catch(() => setDenied(true));
  }, []);

  if (denied) return <div className="p-12 text-center text-neutral-400" data-testid="admin-sessions-denied">Admin access required.</div>;
  if (!cfg) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;

  const toggleDay = (d) => setCfg({ ...cfg, days: cfg.days.includes(d) ? cfg.days.filter((x) => x !== d) : [...cfg.days, d] });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/session-settings", cfg);
      setCfg(data.settings);
      toast.success("Hours saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not save");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 space-y-6" data-testid="admin-sessions-page">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-amber-300/80 mb-2">Admin · 1-on-1 sessions</div>
        <h1 className="font-display text-4xl text-neutral-100">Your hours</h1>
        <p className="text-sm text-neutral-400 mt-2">All times are Pacific. People can book 30-minute slots up to two weeks ahead, at least 24 hours out.</p>
      </div>
      <div className="card-surface p-6 md:p-8 space-y-6">
        <div>
          <div className="text-sm text-neutral-300 mb-3">Days you're available</div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`day-toggle-${d.toLowerCase()}`} aria-pressed={cfg.days.includes(i)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${cfg.days.includes(i) ? "border-amber-400 bg-amber-400 text-black" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-300">
          From <TimeSelect value={cfg.start} onChange={(v) => setCfg({ ...cfg, start: v })} testid="hours-start" options={TIMES.slice(0, 48)} />
          to <TimeSelect value={cfg.end} onChange={(v) => setCfg({ ...cfg, end: v })} testid="hours-end" options={TIMES.slice(1)} />
        </div>
        <div>
          <label className="text-sm text-neutral-300 mb-2 inline-flex items-center gap-2"><Video size={14} /> Your Google Meet link</label>
          <input value={cfg.meet_link} onChange={(e) => setCfg({ ...cfg, meet_link: e.target.value })} placeholder="https://meet.google.com/abc-defg-hij"
            data-testid="meet-link-input" className="wwr-input w-full rounded-xl px-4 py-3 text-sm" />
          <p className="text-xs text-neutral-500 mt-2">Sent in the booking confirmation and the 1-hour reminder.</p>
        </div>
        <BlockedDates dates={cfg.blocked_dates || []} onChange={(v) => setCfg({ ...cfg, blocked_dates: v })} />
        <button onClick={save} disabled={saving} data-testid="save-session-settings" className="btn-primary px-6 py-3 inline-flex items-center gap-2 text-sm disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <Upcoming list={upcoming} />
      <TestimonialsAdmin />
      <StoriesAdmin />
      <ToolkitRatingsAdmin />
    </div>
  );
}
